import type { Option } from '@core/api/types';

/**
 * ============================================================================
 * Sales organizations, which depend on the company codes chosen above them. PURE.
 * ============================================================================
 *
 * `POST /management/sales-orgs/by-company-codes` answers "which sales organizations are mapped to
 * these companies", so the list cannot be fetched until at least one company is picked, and it
 * CHANGES whenever that picking does.
 */

/**
 * The company CODES for a set of selected company IDs.
 *
 * ⚠️ THE PICKER HOLDS UUIDs AND THIS ENDPOINT WANTS CODES. `company_ids` on the create payload
 * takes `"01a013d0-…"`; `company_codes` here takes `"1000"`. The two travel together on
 * `/dropdown/company_code`, so the translation is a lookup — but sending the UUID is not an error,
 * it matches nothing and answers 200 with an empty list, which reads on screen as "this company has
 * no sales organizations" rather than as a bug.
 *
 * Sorted and de-duplicated so the result is a stable query key: an unsorted list would refetch
 * whenever the user happened to tick the same companies in a different order.
 */
export function companyCodesFor(
  selectedCompanyIds: readonly string[],
  companyOptions: readonly Option[],
): string[] {
  const codeById = new Map(companyOptions.map((option) => [option.id, option.code]));

  const codes = new Set<string>();
  for (const id of selectedCompanyIds) {
    const code = codeById.get(id);
    if (code) codes.add(code);
  }
  return [...codes].sort();
}

/**
 * The selection, narrowed to what is still on offer.
 *
 * ⚠️ `hasLoaded` IS THE WHOLE POINT OF THIS FUNCTION. Filtering a selection against a list that has
 * not arrived yet filters it against NOTHING, which silently empties it — the same shape of bug as
 * a dependent dropdown that clears itself while its options are still in flight. So while the
 * answer is outstanding the selection is returned untouched, and it is narrowed only once a real
 * list for the current company codes is in hand.
 *
 * Narrowing rather than clearing also matters: dropping one company should cost the user only the
 * sales organizations that belonged to it, not every choice they had made.
 */
export function retainOfferedSalesOrgs(
  selected: readonly string[],
  offered: readonly Option[],
  hasLoaded: boolean,
): string[] {
  if (!hasLoaded) return [...selected];

  const offeredIds = new Set(offered.map((option) => option.id));
  return selected.filter((id) => offeredIds.has(id));
}

/**
 * The rows of `POST /management/sales-orgs/by-company-codes`, as options.
 *
 * ⚠️ PARSED HERE RATHER THAN BY `normalizeDropdownResponse`, WHICH GETS THE CODE WRONG. A row is:
 *
 *     {company_code: "1000", sales_org_id: "01a06b0f-…",
 *      sales_org_code: "1000", sales_org_name: "Scientechnic Corporate"}
 *
 * That normaliser sweeps for `<something>_id` and `<something>_name` but has NO `<something>_code`
 * sweep, so with no bare `code` key it falls back to the id — putting a 36-character UUID everywhere
 * a short code belongs. It was visible on screen against live data and invisible to a fixture that
 * had invented codes.
 *
 * Adding that sweep to the shared normaliser would be the general fix, and it is deliberately NOT
 * done here: `option.code` is what several payloads send, and rows that currently fall back to the
 * id would start sending a different value to endpoints this change has no business touching. A
 * known response is parsed explicitly instead.
 */
export function readSalesOrgs(response: unknown): Option[] {
  const body = response as { data?: unknown } | undefined;
  const rows = Array.isArray(body?.data) ? body.data : Array.isArray(response) ? response : [];

  const options: Option[] = [];
  for (const entry of rows) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;

    const id = text(row['sales_org_id']) || text(row['id']);
    if (!id) continue;

    const code = text(row['sales_org_code']) || text(row['code']);
    const name = text(row['sales_org_name']) || text(row['name']) || text(row['value']);

    options.push({
      id,
      /* Falls back to the id only when there is genuinely no code — the picker hides it then. */
      code: code || id,
      label: name || code || id,
      raw: entry,
    });
  }
  return options;
}

/** A non-empty primitive as text; anything else is absent. */
function text(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}
